/**
 * Client-side rules for the auth forms. They mirror the backend request schemas
 * (src/validators/authFields.ts) so users see a translated message instead of a
 * generic "could not be accepted" error. Every function returns the i18n key of
 * the first failing rule, or null.
 */

export const EMAIL_MAX_LENGTH = 254;
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;
export const NAME_MAX_LENGTH = 100;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** E-mail address (any auth form). */
export function getEmailErrorKey(email: string): string | null {
  const value = email.trim();
  if (!value) return 'auth.emailRequired';
  if (value.length > EMAIL_MAX_LENGTH) return 'auth.emailTooLong';
  if (!EMAIL_PATTERN.test(value)) return 'auth.emailInvalid';
  return null;
}

/** Password of an existing account (sign-in): presence and maximum length only. */
export function getLoginPasswordErrorKey(password: string): string | null {
  if (!password) return 'auth.passwordRequired';
  if (password.length > PASSWORD_MAX_LENGTH) return 'auth.passwordTooLong';
  return null;
}

/**
 * A password being set (sign-up, reset): the Cognito pool policy — 8 to 256
 * characters with an upper-case letter, a lower-case letter, a digit and a
 * special character.
 */
export function getNewPasswordErrorKey(password: string): string | null {
  if (password.length === 0) return 'auth.passwordRequired';
  if (password.length < PASSWORD_MIN_LENGTH) return 'auth.passwordTooShort';
  if (password.length > PASSWORD_MAX_LENGTH) return 'auth.passwordTooLong';
  if (!/[A-Z]/.test(password)) return 'auth.passwordNeedsUppercase';
  if (!/[a-z]/.test(password)) return 'auth.passwordNeedsLowercase';
  if (!/[0-9]/.test(password)) return 'auth.passwordNeedsDigit';
  if (!/[^A-Za-z0-9]/.test(password)) return 'auth.passwordNeedsSpecial';
  return null;
}

/** First or last name; `requiredKey` is the field's "is required" message. */
export function getNameErrorKey(name: string, requiredKey: string): string | null {
  const value = name.trim();
  if (!value) return requiredKey;
  if (value.length > NAME_MAX_LENGTH) return 'auth.nameTooLong';
  return null;
}
